import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  RemoveUserFilterPageIdQueryParamsFromCache,
  SaveUserFilterQueryParamsInCache,
} from './user-filter-settings.actions';
import { captureException } from '@sentry/angular';
import {
  PageFilterIdentifier,
  PERSISTENT_FILTERS_CONFIG,
} from '../../constants/page-filter-identifiers';
import {
  FilterParamQueries,
  UserId,
} from '../../interfaces/filters.interfaces';

export interface UserFilterSettingsStateModel {
  filters: Record<UserId, Record<PageFilterIdentifier, FilterParamQueries>>;
}

@State<UserFilterSettingsStateModel>({
  name: 'userFilterSettings',
  defaults: {
    filters: {},
  },
})
@Injectable()
export class UserFiltersSettingsState {
  static getUserFilterValues(
    userId: UserId,
    pageId: PageFilterIdentifier,
  ): (state: UserFilterSettingsStateModel) => FilterParamQueries {
    return createSelector(
      [UserFiltersSettingsState],
      (state: UserFilterSettingsStateModel) => {
        return state.filters?.[userId]?.[pageId] ?? {};
      },
    );
  }

  @Action(SaveUserFilterQueryParamsInCache)
  setUserFilterUrlValue(
    ctx: StateContext<UserFilterSettingsStateModel>,
    action: SaveUserFilterQueryParamsInCache,
  ): void {
    try {
      const state = ctx.getState();
      const { userId, pageId, filterParamQueries } = action;
      ctx.patchState({
        filters: {
          ...state.filters,
          [userId]: {
            ...state.filters[userId],
            [pageId]: filterParamQueries,
          },
        },
      });
    } catch (e) {
      captureException(e);
    }
  }

  @Action(RemoveUserFilterPageIdQueryParamsFromCache)
  removeUserFilterUrlValue(
    ctx: StateContext<UserFilterSettingsStateModel>,
    action: RemoveUserFilterPageIdQueryParamsFromCache,
  ): void {
    try {
      const state = ctx.getState();
      const { userId, pageId } = action;

      // Determine which filters to keep
      const filtersToKeep = PERSISTENT_FILTERS_CONFIG[pageId] ?? []; // Default fallback

      const currentPageFilters = state.filters[userId]?.[pageId] || {};

      const preservedFilters = this.extractPersistentFilters(
        currentPageFilters,
        filtersToKeep,
      );

      ctx.patchState({
        filters: {
          ...state.filters,
          [userId]: {
            ...state.filters[userId],
            [pageId]: preservedFilters,
          },
        },
      });
    } catch (e) {
      captureException(e);
    }
  }

  // Helper method to extract only specified filters
  private extractPersistentFilters(
    filters: Record<string, string>,
    keysToKeep: string[],
  ): Record<string, string> {
    return keysToKeep.reduce(
      (acc, key) => {
        if (filters[key] !== undefined) {
          acc[key] = filters[key];
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }
}
